import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Key, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export const PasswordGenerator = () => {
  const [length, setLength] = useState(16);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [password, setPassword] = useState<string>("");

  const generatePassword = () => {
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const symbols = "!@#$%^&*()_+-=[]{}|;:,.<>?";

    let chars = "";
    if (includeUppercase) chars += uppercase;
    if (includeLowercase) chars += lowercase;
    if (includeNumbers) chars += numbers;
    if (includeSymbols) chars += symbols;

    if (chars.length === 0) {
      toast.error("Please select at least one character type");
      return;
    }

    const array = new Uint32Array(length);
    crypto.getRandomValues(array);

    let result = "";
    for (let i = 0; i < length; i++) {
      result += chars[array[i] % chars.length];
    }

    setPassword(result);
    toast.success("Password generated!");
  };

  const copyPassword = () => {
    if (password) {
      navigator.clipboard.writeText(password);
      toast.success("Password copied to clipboard!");
    }
  };

  const getStrength = () => {
    let strength = 0;
    if (includeUppercase) strength++;
    if (includeLowercase) strength++;
    if (includeNumbers) strength++;
    if (includeSymbols) strength++;
    
    if (length < 8) return { label: "Weak", color: "text-red-500" };
    if (length < 12 && strength < 3) return { label: "Fair", color: "text-yellow-500" };
    if (length < 16 || strength < 4) return { label: "Good", color: "text-blue-500" };
    return { label: "Strong", color: "text-green-500" };
  };

  const strength = getStrength();

  return (
    <Card className="p-6 shadow-[var(--shadow-medium)]">
      <div className="flex items-center gap-2 mb-6">
        <Key className="w-6 h-6 text-primary" />
        <h2 className="text-2xl font-bold text-foreground">Password Generator</h2>
      </div>

      <div className="space-y-6 mb-4">
        <div>
          <div className="flex justify-between items-center mb-2">
            <Label>Password Length: {length}</Label>
            <span className={`text-sm font-medium ${strength.color}`}>
              {strength.label}
            </span>
          </div>
          <Slider
            value={[length]}
            onValueChange={(value) => setLength(value[0])}
            min={6}
            max={32}
            step={1}
            className="mt-2"
          />
        </div>

        <div className="space-y-3">
          {[
            { label: "Uppercase (A-Z)", checked: includeUppercase, onChange: setIncludeUppercase },
            { label: "Lowercase (a-z)", checked: includeLowercase, onChange: setIncludeLowercase },
            { label: "Numbers (0-9)", checked: includeNumbers, onChange: setIncludeNumbers },
            { label: "Symbols (!@#$...)", checked: includeSymbols, onChange: setIncludeSymbols }
          ].map((option) => (
            <div key={option.label} className="flex items-center space-x-2">
              <Checkbox
                id={option.label}
                checked={option.checked}
                onCheckedChange={(checked) => option.onChange(checked === true)}
              />
              <Label htmlFor={option.label} className="cursor-pointer">
                {option.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <Button onClick={generatePassword} className="w-full mb-4">
        <RefreshCw className="w-4 h-4 mr-2" />
        Generate Password
      </Button>

      {password && (
        <div className="bg-secondary rounded-lg p-4">
          <div className="flex items-center justify-between gap-2">
            <code className="text-sm font-mono text-foreground break-all flex-1">
              {password}
            </code>
            <Button
              size="sm"
              variant="ghost"
              onClick={copyPassword}
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};
